(() => {
  const STORAGE_KEY = 'kuMaluAffiliateAttribution';
  const COOKIE_NAME = 'ku_malu_affiliate';
  const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;
  const TRACKED_PARAM_KEYS = [
    'affiliate_id',
    'affiliate',
    'aff',
    'ref',
    'sca_ref',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
  ];
  const AFFILIATE_PARAM_KEYS = ['affiliate_id', 'affiliate', 'aff', 'ref', 'sca_ref'];

  const readParams = () => {
    const searchParams = new URLSearchParams(window.location.search);
    return TRACKED_PARAM_KEYS.reduce((params, key) => {
      const value = searchParams.get(key);
      if (value) params[key] = value.trim();
      return params;
    }, {});
  };

  const normalizeAffiliateId = (value) =>
    (value || '')
      .replace(/^VIP-/i, '')
      .replace(/[^a-z0-9_-]/gi, '')
      .slice(0, 32)
      .toUpperCase();

  const getAffiliateParam = (params) => {
    for (const key of AFFILIATE_PARAM_KEYS) {
      const affiliateId = normalizeAffiliateId(params[key]);
      if (affiliateId) return { key, affiliateId, rawValue: params[key] };
    }

    return null;
  };

  // ✅ CONSENT GATE — restored 2026-08-03, client confirmed EU + US selling.
  //
  // Affiliate attribution is a marketing purpose, so under GDPR/ePrivacy the
  // cookie and localStorage record need consent before being written. This was
  // removed on 2026-07-31 when the only market was Thailand; that premise is
  // gone now that the product is being sold into the EU and US.
  //
  // WHAT IS AND IS NOT GATED:
  //   Gated   - the cookie and the localStorage record (storage on the device).
  //   Ungated - tagging the checkout link during THIS page view. Nothing is
  //             stored, so an affiliate visitor who buys immediately is still
  //             attributed. This is what keeps the gate from costing every
  //             referral, which is why the first attempt was abandoned.
  //
  // The API is often not loaded on first paint, so `marketingAllowed()` returns
  // null rather than false; we hold the value in memory and persist it if and
  // when `visitorConsentCollected` reports consent.
  //
  // 🔴 This only works if the cookie banner is actually enabled for the visitor's
  // region. Shopify shows it automatically once an EU market exists (Settings ->
  // Customer privacy, "Use automated settings"). Verify the banner appears from
  // an EU IP before treating this as compliant.
  // Holds the current visit's attribution when consent has not (yet) been given.
  // Same-session checkout tagging uses this, so a visitor who arrives on an
  // affiliate link and buys immediately is still attributed without anything
  // being written to their device.
  let pendingAttribution = null;

  const consentApi = () => (window.Shopify && window.Shopify.customerPrivacy) || null;

  // true = consented, false = refused, null = cannot tell yet (API not loaded).
  const marketingAllowed = () => {
    const api = consentApi();
    if (!api || typeof api.marketingAllowed !== 'function') return null;
    try {
      return api.marketingAllowed();
    } catch (error) {
      return null;
    }
  };

  const persistAttribution = (attribution) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
    } catch (error) {
      // Storage can be blocked by privacy settings; the cart URL still gets tagged.
    }

    document.cookie = `${COOKIE_NAME}=${encodeURIComponent(
      attribution.affiliateId
    )}; Max-Age=${Math.round(MAX_AGE_MS / 1000)}; Path=/; SameSite=Lax; Secure`;
  };

  const saveAttribution = (attribution) => {
    if (marketingAllowed() === true) {
      pendingAttribution = null;
      persistAttribution(attribution);
      return;
    }

    // Not consented, or the API has not loaded yet. Nothing is written to the
    // device; the visit still works via pendingAttribution, and the listener
    // below persists retroactively the moment consent is granted.
    pendingAttribution = attribution;
  };

  const readStoredAttribution = () => {
    try {
      const storedValue = window.localStorage.getItem(STORAGE_KEY);
      if (!storedValue) return null;

      const attribution = JSON.parse(storedValue);
      // A tampered/legacy record with a missing capturedAt yields NaN, and every
      // NaN comparison is false — so check the timestamp is a number first or an
      // expired record would survive forever.
      const capturedAt = Number(attribution.capturedAt);
      if (!attribution.affiliateId || !Number.isFinite(capturedAt) || Date.now() - capturedAt > MAX_AGE_MS) {
        window.localStorage.removeItem(STORAGE_KEY);
        return null;
      }

      return attribution;
    } catch (error) {
      return null;
    }
  };

  const getAttribution = () => {
    const params = readParams();
    const affiliateParam = getAffiliateParam(params);

    if (affiliateParam) {
      const attribution = {
        affiliateId: affiliateParam.affiliateId,
        sourceParam: affiliateParam.key,
        // Capped and stripped of control characters: these land in order
        // attributes visible in the admin, so keep them boring.
        sourceValue: String(affiliateParam.rawValue || '')
          .replace(/[\u0000-\u001F\u007F]/g, '')
          .slice(0, 64),
        params,
        // Origin + path only. The query string is attacker-controlled and adds
        // nothing to reconciliation.
        landingUrl: window.location.origin + window.location.pathname,
        capturedAt: Date.now(),
      };

      saveAttribution(attribution);
      return attribution;
    }

    return readStoredAttribution() || pendingAttribution;
  };

  const setUrlAttribute = (url, name, value) => {
    if (!value) return;
    url.searchParams.set(`attributes[${name}]`, value);
  };

  // NOTE: these attributes are LANDING attribution only — a reconciliation trail,
  // not a commission record. The affiliate app (UpPromote) is authoritative for
  // commissions; it attributes by its own coupon/link tracking. Do not pay out
  // from these values as well or affiliates get counted twice.
  const tagCheckoutLink = (link, attribution) => {
    const url = new URL(link.getAttribute('href'), window.location.origin);

    setUrlAttribute(url, 'Affiliate ID', attribution.affiliateId);
    setUrlAttribute(url, 'Affiliate Source', attribution.sourceParam || 'stored');
    setUrlAttribute(url, 'Affiliate Ref', attribution.sourceValue || attribution.affiliateId);
    setUrlAttribute(url, 'Affiliate Landing URL', attribution.landingUrl);
    setUrlAttribute(url, 'Attribution Captured At', new Date(attribution.capturedAt).toISOString());

    link.setAttribute('href', url.pathname + url.search + url.hash);
  };

  const appendWaitlistTags = (input, attribution) => {
    const existingTags = input.value
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
    const affiliateTag = `ku-affiliate-${attribution.affiliateId.toLowerCase()}`;
    const sourceTag = `ku-affiliate-source-${(attribution.sourceParam || 'stored').toLowerCase()}`;
    const tags = Array.from(new Set([...existingTags, 'ku-affiliate', affiliateTag, sourceTag]));

    input.value = tags.join(',');
  };

  const applyAffiliateAttribution = () => {
    const attribution = getAttribution();
    if (!attribution) return;

    document.querySelectorAll('[data-ku-affiliate-checkout]').forEach((link) => {
      tagCheckoutLink(link, attribution);
    });

    document.querySelectorAll('[data-ku-affiliate-tags]').forEach((input) => {
      appendWaitlistTags(input, attribution);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyAffiliateAttribution, { once: true });
  } else {
    applyAffiliateAttribution();
  }

  // Shopify fires this after the visitor answers the cookie banner. If they
  // accept, persist the attribution we were holding in memory and re-tag the
  // page, so a referral is not lost just because consent arrived a moment late.
  // Persist whatever we are holding, if consent now allows it.
  //
  // 🔴 THIS MUST RUN AFTER loadFeatures, NOT ONLY ON visitorConsentCollected.
  // That event fires when a visitor answers the banner — so in a region where no
  // banner is shown and consent is granted by default, it never fires at all.
  // Gating solely on it means attribution is held in memory and then thrown away
  // on every visit: the 90-day window silently becomes zero and returning
  // visitors lose their affiliate. Caught on the live site 2026-08-03, where
  // marketingAllowed() was true yet nothing had been written.
  const flushPendingAttribution = () => {
    if (pendingAttribution && marketingAllowed() === true) {
      persistAttribution(pendingAttribution);
      pendingAttribution = null;
    }
  };

  document.addEventListener('visitorConsentCollected', () => {
    flushPendingAttribution();
    applyAffiliateAttribution();
  });

  // The Customer Privacy API is not always on the page at first paint, so the
  // initial marketingAllowed() often returns null. Request it explicitly and
  // re-check in the callback, which covers the no-banner/auto-consent case.
  if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
    try {
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        () => {
          flushPendingAttribution();
        }
      );
    } catch (error) {
      // Nothing to do; the gate stays closed until consent can be confirmed.
    }
  }

})();

(() => {
  const codeTarget = document.querySelector('[data-ku-vip-code]');
  if (!codeTarget) return;

  const params = new URLSearchParams(window.location.search);
  const normalize = (value) =>
    (value || '')
      .replace(/[^a-z0-9_-]/gi, '')
      .slice(0, 32)
      .toUpperCase();

  // The confirmation email links here with ?code=VIP-<random>, built from the
  // order's confirmation_number.
  //
  // There is deliberately NO ?order= fallback. It used to derive VIP-1001 from
  // the order number, which meant anyone could guess a valid-looking code and
  // read the reservation count off their own. The email is now the only source
  // of a code; a missing/invalid one falls through to the placeholder below,
  // which is the correct outcome rather than inventing a guessable code.
  const vipCode = normalize(params.get('code') || params.get('vip_code'));

  // Without a code in the URL the section keeps its "Sent by email" placeholder.
  if (vipCode) codeTarget.textContent = vipCode;
})();
